<script type="ts" setup>
import {
  ref, computed, toRaw, watch, onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import {
  ElLink, ElTabs, ElTabPane, ElIcon,
} from 'element-plus';
import { Download } from '@element-plus/icons-vue';
import AppMainContent from '@/components/AppMainContent.vue';
import ActivityTable from '@/views/activity/ActivityTable.vue';
import ActivityTableFilters from '@/views/activity/ActivityTableFilters.vue';
import SelectOrganizationWarning from '@/components/SelectOrganizationWarning.vue';
import { formatSocialShopSourceForExport } from '@/views/activity/socialShopAttribution';
import shared from '../manage-users-and-organizations/shared';

const store = useStore();
const router = useRouter();
const isOrgAdmin = store.getters['users/isOrgAdmin'];
const route = useRoute();

const { doGetStripeAuthorizeUrl, getOrganization } = shared();

const showSelectOrganization = ref(false);
const activeTransactionTab = ref('sticker');

const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

const fetchActivityData = async (_orgId) => {
  // Clear all transaction data
  store.commit('activityLogs/MUTATE_STICKER_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_STRIPE_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_W2W_TRANSACTIONS', []);
  store.dispatch('activityLogs/SET_SELECTED_LOGS', []);

  // Fetch data for the current tab with date range
  await store.dispatch('activityLogs/FETCH_CURRENT_TAB_DATA');
};

// Watch for date changes to refetch data
watch(() => store.getters['activityLogs/GET_DATE'], () => {
  if (!showSelectOrganization.value) {
    fetchActivityData();
  }
}, { deep: true });

const updateSelectOrgWarning = () => {
  if (isOrgAdmin) {
    if ((!route.params.id || route.params.id === ':id' || route.params.id === 'ORG_ID' || route.params.id === 'undefined')
          && (!selectedOrganization.value || !selectedOrganization.value.id)) {
      showSelectOrganization.value = true;
    } else {
      showSelectOrganization.value = false;

      if (route.params.id && route.params.id !== ':id' && route.params.id !== 'ORG_ID' && route.params.id !== 'undefined') {
        fetchActivityData(route.params.id);
      } else if (selectedOrganization.value && selectedOrganization.value.id) {
        fetchActivityData(selectedOrganization.value.id);
      }
    }
  } else {
    fetchActivityData();
    showSelectOrganization.value = false;
  }
};

onMounted(() => {
  updateSelectOrgWarning();
});

watch(selectedOrganization, (newOrg, oldOrg) => {
  const newOrgId = newOrg?.id;
  const oldOrgId = oldOrg?.id;

  if (newOrgId !== oldOrgId) {
    if (isOrgAdmin && newOrgId) {
      if (route.path.includes('/activity/')) {
        const newPath = `/activity/${newOrgId}`;
        if (route.path !== newPath) {
          router.push(newPath);
        } else {
          fetchActivityData(newOrgId);
        }
      } else {
        updateSelectOrgWarning();
      }
    } else if (!isOrgAdmin) {
      fetchActivityData();
    } else {
      updateSelectOrgWarning();
    }
  }
});

watch(() => route.params.id, (newId, oldId) => {
  if (newId !== oldId && newId && newId !== ':id' && newId !== 'ORG_ID' && newId !== 'undefined') {
    fetchActivityData(newId);
  }
});

const filteredTransactions = computed(() => store.getters['activityLogs/GET_FILTERED_TRANSACTIONS'] || []);
const allTransactions = computed(() => store.getters['activityLogs/GET_CURRENT_TRANSACTIONS'] || []);
const filteredTotal = computed(() => filteredTransactions.value
  .reduce((sum, row) => sum + (Number(row.amount) || 0), 0));

const currentTab = computed(() => store.getters['activityLogs/GET_CURRENT_TAB']);

const isExporting = ref(false);
// Helper function to extract EIN from details text
const extractEIN = (details) => {
  if (!details || typeof details !== 'string') {
    return '';
  }

  // Look for patterns like ": 840-520851555" after colon
  // This covers various ID formats: XX-XXXXXXX, XXX-XXXXXXX, XXX-XXXXXXXXX
  const einRegex = /:\s*(\d{2,3}-\d{7,9})\b/;
  const match = details.match(einRegex);

  if (match) {
    return match[1];
  }
  return '';
};

const onExportCSV = () => {
  const includeShopSource = currentTab.value === 'sticker';
  const headers = [
    'Transaction',
    'Date',
    'User',
    'Total',
    'Item',
    'Details',
    ...(includeShopSource ? ['Shop Source'] : []),
    'Email',
    'Type',
    'Processed',
    'EIN',
  ];
  // Export filtered transactions from the current tab (applies search and date filters)
  const filteredTransactions = toRaw(store.getters['activityLogs/GET_FILTERED_TRANSACTIONS']);
  const rows = filteredTransactions;
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => {
      const transId = `#${row.transactionId.slice(-5).toString()}`;
      const processedStatus = row.isProcessed !== undefined ? (row.isProcessed ? 'Yes' : 'No') : 'N/A';
      const ein = extractEIN(row.details);
      const values = [
        transId,
        new Date(row.date).toLocaleString(),
        row.userName,
        row.amount.toFixed(2),
        row.item,
        row.details,
        ...(includeShopSource ? [formatSocialShopSourceForExport(row)] : []),
        row.email,
        row.type,
        processedStatus,
        ein,
      ];
      return values.map((value) => `"${value}"`).join(',');
    }),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const now = new Date();
  const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
  const fileName = `activity_logs_${dateTimeString}.csv`;

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  isExporting.value = false;
};

async function onBoardOnStripe() {
  const organization = await getOrganization(route.params.id);
  if (!organization.stripe_onboarded && route.params.id) {
    const stripeLink = await doGetStripeAuthorizeUrl(route.params.id);
    window.location.href = stripeLink;
  } else {
    window.location.href = 'https://connect.stripe.com/';
  }
}

// Handle transaction tab changes
const handleTransactionTabChange = (tabName) => {
  activeTransactionTab.value = tabName;
  store.dispatch('activityLogs/SET_CURRENT_TAB', tabName);
};
</script>

<template lang="pug">
app-main-content
  .c-activity-logs
    //- Header: title on the left; date range, links and Export on the right
    section.c-activity-logs__header
      .c-activity-logs__header-content
        h1.c-activity-logs__heading Transaction Log
        p.c-activity-logs__subtitle Monitor all financial transactions and activity
      .c-activity-logs__header-actions
        ActivityTableFilters(v-if="!showSelectOrganization" :show-date-only="true")
        el-link(
          v-if="isOrgAdmin"
          href="https://causes.benevity.org/"
          target="_blank"
          :underline="false"
          class="c-activity-action-link"
        ) Benevity Payouts
        el-link(
          v-if="!showSelectOrganization"
          @click="onBoardOnStripe"
          :underline="false"
          class="c-activity-action-link"
        ) Stripe Account
        el-button.c-activity-logs__export-btn(
          type="primary"
          @click="onExportCSV"
          :loading="isExporting"
          size="large"
        )
          el-icon.me-2
            Download
          span Export

    //- Section toggle and transaction type tabs share one row
    section.c-activity-logs__nav-section(v-if="!showSelectOrganization")
      .c-activity-logs__nav-wrapper
        .c-activity-nav
          button.c-activity-nav-link.c-activity-nav-link--active(disabled) Transactions
          button.c-activity-nav-link(@click="$router.push('/manage-resources/todos')") Resources
        .c-activity-logs__nav-divider
        el-tabs(
          v-model="activeTransactionTab"
          @tab-change="handleTransactionTabChange"
          class="c-transaction-tabs"
        )
          el-tab-pane(label="Sticker" name="sticker")
          el-tab-pane(label="Stripe" name="stripe")
          el-tab-pane(label="Benevity" name="benevity")
          el-tab-pane(label="Benevity W2W credit" name="benevity-w2w")

    SelectOrganizationWarning(v-if="showSelectOrganization" type="logs")

    //- Table card with its search/sort row on top
    section.c-activity-table-section(v-if="!showSelectOrganization")
      article.c-activity-table-section__filters
        ActivityTableFilters
      ActivityTable

    //- Results count
    .c-activity-logs__results(v-if="!showSelectOrganization && filteredTransactions.length")
      span Showing {{ filteredTransactions.length }} of {{ allTransactions.length }} transactions
      span.c-activity-logs__results-total Total: ${{ filteredTotal.toFixed(2) }}
</template>

<style lang="scss" scoped>
$ui-font: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue',
  Arial, sans-serif;

// Color Variables - Match Dashboard.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-900: #111827;
$color-purple-text: #4349dd;
$muted: #ececf0;

// Card tokens from the client design (shadcn Card: rounded-xl + 10% black border)
$card-border: rgba(0, 0, 0, 0.1);
$card-radius: 14px;

.c-activity-logs {
  // Client design uses the system sans stack instead of the global brand font
  font-family: $ui-font;
  --el-font-family: #{$ui-font};
  min-height: 100vh;
  padding: 2rem;
  background-color: $gray-50;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: #0a0a0a;
    margin: 0;
    line-height: 2.25rem;
  }

  &__subtitle {
    font-size: 1rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5rem;
  }

  &__export-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
    border-radius: 8px;
    background-color: $color-purple-text !important;
    border-color: $color-purple-text !important;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%) !important;
      border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    }

    :deep(span) {
      font-weight: 500;
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }

  &__nav-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  &__nav-divider {
    width: 1px;
    height: 24px;
    background: $card-border;
  }

  &__results {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: $gray-600;
  }

  &__results-total {
    color: $gray-500;
  }
}

// Navigation Tabs (shadcn TabsList style)
.c-activity-nav {
  display: inline-flex;
  height: 36px;
  align-items: center;
  justify-content: center;
  background-color: $muted;
  border-radius: 12px;
  padding: 3px;
  gap: 0;
  overflow-x: auto;
}

.c-activity-nav-link {
  display: inline-flex;
  height: calc(100% - 1px);
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: #0a0a0a;
  cursor: pointer;
  transition: color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
  user-select: none;
  background: none;

  &--active {
    background-color: white;
  }

  &:disabled {
    cursor: default;
  }
}

.c-activity-table-section {
  background: white;
  border-radius: $card-radius;
  border: 1px solid $card-border;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  &__filters {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid $card-border;
  }
}

.c-activity-action-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: $color-teal-accent;
  transition: color 0.2s;

  &:hover {
    color: #01a89c;
  }
}

// Transaction tabs: same pill look as the design's TabsList
.c-transaction-tabs {
  :deep(.el-tabs__header) {
    display: inline-block;
    max-width: 100%;
    margin: 0;
    padding: 3px;
    background: $muted;
    border-radius: 12px;
    border-bottom: none;
  }

  :deep(.el-tabs__nav-wrap) {
    margin-bottom: 0;

    &::after {
      height: 0;
    }
  }

  :deep(.el-tabs__item) {
    height: 29px;
    padding: 4px 16px;
    line-height: 20px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #0a0a0a;
    border: 1px solid transparent;
    border-radius: 12px;

    &.is-active {
      background: white;
      color: #0a0a0a;
    }

    &:hover {
      color: #0a0a0a;
    }
  }

  :deep(.el-tabs__active-bar) {
    display: none;
  }

  :deep(.el-tabs__nav) {
    border: none;
  }
}

// Checkbox styling
:deep(.el-checkbox__label) {
  display: none;
}

@media (max-width: 768px) {
  .c-activity-logs {
    padding: 1rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__nav-divider {
      display: none;
    }
  }

  .c-activity-nav {
    max-width: 100%;
  }

  // Phones: the global CMS rule shares the header row equally between all actions
  // (flex: 1 1 0), which squeezed the date range under the links. Give it its own row.
  // (Root of a fragment component, hence :deep(); the extra class outranks the global rule.)
  .c-activity-logs .c-activity-logs__header-actions :deep(.c-date-range-card) {
    flex: 1 1 100%;
  }

  // Phones: the transaction type tabs scroll sideways instead of running off the screen
  .c-activity-logs__nav-wrapper,
  .c-transaction-tabs {
    min-width: 0;
    max-width: 100%;
  }

  .c-transaction-tabs :deep(.el-tabs__nav-scroll) {
    overflow-x: auto;
    scrollbar-width: none;
  }
}
</style>
